/**
 * How big the plates on a till's rate board are, given how many there are.
 *
 * **A government garden counter has a rate board on the wall, not a card grid.**
 * `grid-cols-[repeat(auto-fit,minmax(13rem,1fr))]` is a supermarket grid, and
 * this shop sells two things: measured at the real window size — about 1000x625
 * CSS px, so `max-w-[84rem]` never binds — the goods column was ~480px holding
 * one row of two tiles with ~400px of nothing beneath it. The same failure
 * `docs/design-language.md` records in "The margin and the band", one screen
 * over.
 *
 * So the floor moves with the stock. Few items are few large plates that use the
 * field; forty stay the dense board they already are. Two items stops being an
 * embarrassment and becomes a short board.
 *
 * **Every class here is a complete literal and none is assembled.** Tailwind
 * scans source text, so a class built by interpolation is a class stripped from
 * the bundle — which `pnpm build` in the gate is the check for, and which no
 * unit test reaches.
 */

/** The four class strings a board tier decides. */
export interface RateBoardScale {
  /** The track floor. `auto-fit` throughout — see {@link rateBoardScale}. */
  readonly columns: string;
  /**
   * The row band.
   *
   * `1fr` as the maximum on the two smaller tiers is what closes the *vertical*
   * void, which is the one that was actually there: free space in the grid goes
   * into the rows rather than under them, so two items are two plates that reach
   * the foot of the board. The dense tier keeps `auto`, because forty rows
   * sharing free space would be forty plates nobody asked to be taller.
   *
   * **The floor is `5rem` on every tier and only the growth changes**, and it
   * stays there. It was measured down twice from `9rem` against a board that
   * resolved to 130px at the real window once the foot-anchored strip had taken
   * its height: a 144px row put a scrollbar on a shop with two things in it, and
   * 96px cut every plate through its price.
   *
   * **Issue #217 raised it and then put it back, and that round trip is the
   * thing worth keeping.** The argument for raising it was that a `few` plate
   * sets 68px of type and asks for 32px of `py-4` around it, so an 80px row
   * compresses its stated padding to 6px a side — which is true. The argument
   * *given* for raising it was that the plate overflowed its own border, and
   * that was wrong: measured across 36 plate boxes on both counters at both
   * window sizes, the content does not cross the border at any floor. What had
   * been seen was the scrolling *region* cutting the second row, and **a taller
   * floor makes that worse, not better** — it is more pixels per row in a box
   * whose height is fixed by everything else on the screen.
   *
   * On `SellGoods` that cost 48px of extra scroll. On `SellAdmission`, which
   * shares this file and is the gate, it cost more: at 1000x625 a four-item gate
   * board **stopped fitting** in its resting state — 281px of content in a 266px
   * region, cutting the Activities row on the screen that sells admission all
   * day — and an eight-item board gained 32px at both window sizes. A cut row at
   * the gate is worse than a tight plate at the counter, so the padding stays
   * compressed and the row stays whole.
   *
   * `1fr` is then what grows the rows into whatever room there is, which is
   * where the padding comes back on its own: measured on the counter, two items
   * are 250px plates at 1000x625 and 425px at 1280x800, and four are 117px and
   * 205px. The floor only binds on a board that is already short.
   */
  readonly rows: string;
  /** The name, which is the quiet half. */
  readonly name: string;
  /**
   * The price, which is the loud one.
   *
   * `font-mono` and `numeric` are stated at the call site with these, because
   * `docs/design-language.md` reserves mono for "anything read aloud and typed
   * back in" and a price at a counter is read aloud every time.
   */
  readonly price: string;
}

/**
 * Few enough that the field is mostly empty, and the plates should take it.
 *
 * Four rather than two, because the tier has to survive the shop gaining an
 * item: a park that adds a third fridge magnet should not have its board
 * silently collapse to the dense grid.
 */
const FEW = 4;

/** Past this the board is a board, and the plates stop growing. */
const SOME = 12;

const few: RateBoardScale = {
  /**
   * **`16rem` and not the `18rem` this shipped with, and the difference is the
   * sale column beside it.** `18rem` was measured against a board that ran the
   * whole width of the counter — 928px at 1000x625, three tracks — because the
   * sale was a strip under it. The sale is a 22rem column again (issue #217), so
   * the board is **552px**, and 288px tracks lay **one** column in it: the
   * park's four-item shop came out as four stacked rows, 368px of content in a
   * 258px board, and the tier meant to close the void put a scrollbar on a shop
   * with four things in it. That is the same failure the row band's `5rem` floor
   * records one field down, arrived at from the other direction.
   *
   * 256px tracks lay two columns at 552px and three at 832px, so four items are
   * two rows at the real window and the `1fr` band grows them to 117px each.
   *
   * **It changes nothing on `SellAdmission`, and that was rendered rather than
   * reasoned about.** That board is still full width, where 288px and 256px both
   * lay 3 tracks at 1000x625 and 4 at 1280x800 — measured across nine gate
   * states at each size, every figure identical on both sides of this change.
   */
  columns: 'grid-cols-[repeat(auto-fit,minmax(16rem,1fr))]',
  rows: 'auto-rows-[minmax(5rem,1fr)]',
  name: 'text-xl',
  price: 'text-4xl',
};

const some: RateBoardScale = {
  columns: 'grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]',
  rows: 'auto-rows-[minmax(5rem,1fr)]',
  name: 'text-lg',
  price: 'text-3xl',
};

/** What the board was before this, and what forty items still get. */
const many: RateBoardScale = {
  columns: 'grid-cols-[repeat(auto-fit,minmax(13rem,1fr))]',
  rows: 'auto-rows-[minmax(5rem,auto)]',
  name: 'text-base',
  price: 'text-xl',
};

/**
 * The board's scale for a shop of this size.
 *
 * **`auto-fit` on every tier and never `auto-fill`.** The difference is only
 * visible when there are fewer items than columns, which is exactly the shape
 * this park's shop is in: `auto-fill` holds the empty tracks open, so two items
 * sit as two small buttons against a wall of nothing; `auto-fit` collapses them
 * and the two grow to fill the field.
 *
 * `count` is what is **shown**, not what is stocked — a search that narrows
 * forty items to one should give that one a full plate, because at that moment
 * the board has one thing on it.
 */
export function rateBoardScale(count: number): RateBoardScale {
  if (count <= FEW) return few;
  if (count <= SOME) return some;

  return many;
}

/**
 * The most plates this board will post at once.
 *
 * **A board is a thing you read, and past a certain length it is a thing you
 * scroll — which is the slowest picker there is.** Measured at the real window,
 * 1000x625, with the sale on the right: the board is 552px wide, so the dense
 * tier lays two 218px columns, and a plate row is 96px including its gap. Five
 * hundred items is 250 rows — **11,992px of scroll inside a 173px window**, and
 * a cashier with a customer waiting cannot find "Rag Doll, large" in it.
 *
 * Sixty is 30 rows, about 2,900px. It is not chosen as a number a thumb can
 * cover — it cannot, and pretending otherwise is the mistake this replaces. It
 * is chosen as **the largest shop this board was ever measured against**: forty
 * items is the dense board `rateBoardScale` above is pinned to keep, sixty
 * clears that with room, and past it the board stops claiming to be the whole
 * shop and says how much of it is posted. The way to the other 440 is the field
 * above the board, which matches on code as well as name and is what a scanner
 * types into.
 *
 * **This is why the three tiers still hold at the top end and there is no fourth
 * one.** `rateBoardScale` is asked how big a plate is given how many are on the
 * board, and the cap means it is never asked about more than sixty — which is
 * inside the range the dense tier was measured over. A fourth tier would be a
 * plate size for a board that is never drawn.
 */
export const POSTED_MAX = 60;

/** What the board is posting, and how much of the shop that is. */
export interface PostedBoard<T> {
  /** The plates actually drawn, in the order they were given. */
  readonly plates: readonly T[];
  /** How many there were to post — what the count line is `of`. */
  readonly held: number;
  /** Whether anything was left off, and so whether the count is worth stating. */
  readonly capped: boolean;
}

/**
 * The first {@link POSTED_MAX} of whatever the board was going to draw.
 *
 * **It caps the rendering and nothing else.** The search runs over the whole
 * shop and so does the scan — an item that is not posted is still one Enter
 * away, which is what makes a cap honest rather than a hidden shelf. Narrowing
 * the query narrows what is posted the same way, so a search that finds fewer
 * than sixty things shows every one of them.
 *
 * Generic because this file has no business knowing what a catalogue item is; it
 * knows how many plates a board holds.
 */
export function postToBoard<T>(items: readonly T[]): PostedBoard<T> {
  return {
    plates: items.length <= POSTED_MAX ? items : items.slice(0, POSTED_MAX),
    held: items.length,
    capped: items.length > POSTED_MAX,
  };
}
